import React, {useEffect, useState} from "react";
import {Link, LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import axios from "axios";
import {ExtendedAttributes, FaceAttributes, NPC, Plot} from "../../server/Plot";
import Typography from "@mui/material/Typography";
import {Autocomplete, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, TextField, Tooltip} from "@mui/material";
import Container from "@mui/material/Container";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import {DataGrid, GridColDef} from "@mui/x-data-grid";


export async function loadNPCForId(id: number): Promise<any> {
    const apiEndpoint = "/plot/npc/" + id
    return await axios.get(apiEndpoint).then(({data}) => {
        if (data.face_role == undefined) { data.face_role = {} as FaceAttributes }
        if (data.extended == undefined) { data.extended = {} as ExtendedAttributes }
        return data;
    })
}

export const npcLoader = async ({params}: LoaderFunctionArgs): Promise<NPC | null> => {
    if (typeof params.id !== "undefined") {
        return loadNPCForId(parseInt(params.id));
    } else {
        return null
    }
}

const plotColumns: GridColDef[] = [
    {
        field: 'name', headerName: 'Plot', headerAlign: "center", flex: 1, renderCell: (params) => (
            <Link to={'/plot/' + params.row.id}>{params.value}</Link>
        )
    },
    {field: 'short_desc', headerName: 'Short Description', headerAlign: "center", flex: 2},
    {field: 'status', headerName: 'Status', headerAlign: "center", align: "center", width: 100},
]

export function NPCViewer() {
    const [npc, setNPC] = useState(useLoaderData() as NPC);
    const [editable, setEditable] = useState(false);
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [involvedPlots, setInvolvedPlots] = useState<Plot[]>([]);
    const [castMembers, setCastMembers] = useState<string[]>([]);
    const nav = useNavigate();

    useEffect(() => {
        axios.get(`/plot/npc/${npc.id}/plots`).then(({data}) => setInvolvedPlots(data))
        axios.get('/plot/cast').then(({data}) => setCastMembers(data)).catch(() => {})
    }, [npc.id])

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.ctrlKey && e.key === 'e') { e.preventDefault(); toggleEdit() }
            else if (e.ctrlKey && e.key === 's') { e.preventDefault(); if (editable) saveNPC() }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editable])

    function toggleEdit() {
        setEditable(!editable)
    }

    function saveNPC() {
        const apiEndpoint = "/plot/npc/" + npc.id
        return axios.put(apiEndpoint, npc).then(({data}) => {
            setEditable(false)
            return data;
        })
    }

    async function promoteToFaceRole() {
        await axios.patch(`/plot/npc/${npc.id}/promote`).then(({data}) => {
            setNPC(data)
            setPromoteOpen(false)
        })
    }

    return <React.Fragment>
        <Grid container spacing={1}>

            {/* Header */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" component="h4" align="center">{npc.name}</Typography>
                    <Container sx={{textAlign: "center"}}>
                        <ButtonGroup variant="contained" size="small">
                            <Button title="Edit" onClick={toggleEdit}><EditIcon/></Button>
                            <Button title="Save" disabled={!editable} onClick={saveNPC}><SaveIcon/></Button>
                            <Button title="Promote to Face Role" disabled={npc.type !== 'NPC'} onClick={() => setPromoteOpen(true)}><UpgradeIcon/></Button>
                            <Button title="Monster Stats" disabled={!npc.monsterId} onClick={() => nav(`/monsters/${npc.monsterId}`)}>Stats</Button>
                        </ButtonGroup>
                    </Container>
                </Paper>
            </Grid>

            {/* Basics */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Basics</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Name' disabled={!editable}
                                       defaultValue={npc.name}
                                       onChange={(ev) => { npc.name = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Type' disabled
                                       defaultValue={npc.type}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                disabled={!editable}
                                options={castMembers}
                                defaultValue={npc.extended?.played_by ?? ''}
                                onInputChange={(_, value) => { npc.extended.played_by = value }}
                                renderInput={(params) => <TextField {...params} fullWidth label="Played By"/>}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Appearance */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Appearance</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Species Type' disabled={!editable}
                                       defaultValue={npc.extended?.species_type}
                                       onChange={(ev) => { npc.extended.species_type = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Outward Appearance' disabled={!editable}
                                       multiline minRows={4}
                                       defaultValue={npc.extended?.appearance}
                                       onChange={(ev) => { npc.extended.appearance = ev.target.value }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Personal Details */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Personal Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Personality' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.extended?.personality}
                                       onChange={(ev) => { npc.extended.personality = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Needs or Intentions' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.extended?.needs_intent}
                                       onChange={(ev) => { npc.extended.needs_intent = ev.target.value }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Background */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Background</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Background' disabled={!editable}
                                       multiline minRows={8}
                                       defaultValue={npc.extended?.history}
                                       onChange={(ev) => { npc.extended.history = ev.target.value }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Other Details */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Other Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Groups' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.extended?.groups}
                                       onChange={(ev) => { npc.extended.groups = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Virtues' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.face_role?.virtues}
                                       onChange={(ev) => { npc.face_role.virtues = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Vices' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.face_role?.vices}
                                       onChange={(ev) => { npc.face_role.vices = ev.target.value }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Personal Relations' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={npc.face_role?.personal_relations}
                                       onChange={(ev) => { npc.face_role.personal_relations = ev.target.value }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Involved in Plots */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Involved in Plots</Typography>
                    <DataGrid
                        autoHeight
                        rows={involvedPlots}
                        columns={plotColumns}
                        getRowId={(row) => row.id}
                        density="compact"
                        initialState={{
                            pagination: {paginationModel: {pageSize: 24}},
                        }}
                    />
                </Paper>
            </Grid>

        </Grid>

        <Dialog open={promoteOpen} onClose={() => setPromoteOpen(false)}>
            <DialogTitle>Promote to Face Role?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This will change <strong>{npc.name}</strong> from a Named NPC to a Face Role. This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPromoteOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={promoteToFaceRole}>Promote</Button>
            </DialogActions>
        </Dialog>

    </React.Fragment>
}
